import type { ChangeEvent, KeyboardEvent, ReactNode, Ref } from 'react';
import styles from './InputField.module.css';

interface InputFieldProps {
  label?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  className?: string;
  // Native input
  type?: 'text' | 'number';
  value?: string | number;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  maxLength?: number;
  // For a field with no visible label, e.g. an inline rename in place of text.
  ariaLabel?: string;
  // Lets the owner focus or select the field on mount.
  inputRef?: Ref<HTMLInputElement>;
  // Widened because a clickable custom-content field renders as a <button>.
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement | HTMLButtonElement>) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  list?: string;
  // Custom content rendered inside the input-styled container (colour/icon pickers etc.)
  children?: ReactNode;
  onClick?: () => void;
  active?: boolean;
  // Combobox semantics for a custom-content field that opens a popup, so the
  // control announces what it opens and whether it is currently open.
  ariaHasPopup?: 'listbox' | 'menu' | 'dialog';
  ariaExpanded?: boolean;
  // Lets the owner of a popup move focus back to the trigger when its panel
  // closes, so keyboard focus never falls to the document body.
  triggerRef?: Ref<HTMLButtonElement>;
}

export const InputField = ({
  label,
  required,
  error,
  disabled,
  className,
  type = 'text',
  value,
  onChange,
  maxLength,
  ariaLabel,
  inputRef,
  placeholder,
  min,
  max,
  children,
  onClick,
  active,
  onKeyDown,
  list,
  ariaHasPopup,
  ariaExpanded,
  triggerRef,
}: InputFieldProps) => {
  const containerCls = [
    styles.input,
    error && styles.inputError,
    disabled && styles.inputDisabled,
    children && styles.inputCustom,
    active && styles.inputActive,
  ]
    .filter(Boolean)
    .join(' ');

  const wrapperCls = [styles.wrapper, className].filter(Boolean).join(' ');

  // A clickable custom-content field is a button, so it renders as one:
  // as a div it was unreachable by keyboard, which left the colour and
  // competence pickers mouse-only. Without onClick it stays a plain box.
  const customContent = onClick ? (
    <button
      ref={triggerRef}
      type="button"
      className={containerCls}
      onClick={onClick}
      onKeyDown={onKeyDown}
      disabled={disabled}
      aria-haspopup={ariaHasPopup}
      aria-expanded={ariaExpanded}
    >
      {children}
    </button>
  ) : (
    <div className={containerCls}>{children}</div>
  );

  return (
    <div className={wrapperCls}>
      {label && (
        <span className={styles.label}>
          {label}
          {required && <span className={styles.asterisk}>*</span>}
        </span>
      )}

      {children ? (
        customContent
      ) : (
        <input
          ref={inputRef}
          className={containerCls}
          type={type}
          value={value ?? ''}
          onChange={onChange}
          // A plain input can also own a popup (the competence picker), so its
          // click must reach the handler too — not only the custom-content button.
          onClick={onClick}
          onKeyDown={onKeyDown}
          maxLength={maxLength}
          aria-label={ariaLabel}
          placeholder={placeholder}
          min={min}
          max={max}
          disabled={disabled}
          list={list}
        />
      )}

      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
};
